import * as React from 'react'

import {
  FaIcon,
  faDownloadSolid,
  faRefreshSolid,
  faTrash,
  faXmark,
} from '@/lib/fa'
import Tooltip from '../ToolTip'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  FieldsetSelect,
  Option,
} from '@/modules/settings/components/FieldsetSelect'

export type ChartDetailsFilter = {
  options: Option[]
  value: string
  onChange: (value: string) => void
  searchEnabled?: boolean
}

const ChartDetailsActionBar = ({
  filters,
  selectedCount,
  onDelete,
  deleteDisabled = false,
  deleteBlockedText,
  onClearSelection,
  onRefresh,
  onExport,
  exportDisabled = false,
  elevated = true,
}: {
  filters: ChartDetailsFilter[]
  selectedCount: number
  onDelete: () => void
  deleteDisabled?: boolean
  deleteBlockedText?: string
  onClearSelection: () => void
  onRefresh: () => void
  onExport: () => void
  exportDisabled?: boolean
  elevated?: boolean
}) => {
  const { t } = useLocalize()
  const hasSelection = selectedCount > 0

  return (
    <div
      className={`tw-w-full tw-border-b tw-border-[#e5e6e6] tw-bg-[#fafbff] tw-py-2 ${
        elevated ? 'tw-relative tw-z-[100]' : ''
      }`}
    >
      <div className="tw-flex tw-w-full tw-flex-wrap tw-justify-between tw-gap-3 tw-px-4 tw-py-1.5 md:tw-items-center">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
          {filters.map((filter, index) => (
            <div key={index} className="tw-w-48">
              <FieldsetSelect
                background
                searchEnabled={filter.searchEnabled ?? false}
                options={filter.options}
                value={filter.value}
                handleChange={(value) => filter.onChange(value)}
              />
            </div>
          ))}
        </div>

        <div className="tw-flex tw-items-center tw-gap-4">
          {hasSelection && (
            <>
              <span className="tw-text-sm tw-font-medium tw-text-primary">
                {selectedCount} {t('driverModule.chart-selected')}
              </span>
              <Tooltip
                text={t('driverModule.chart-clear-selection')}
                onClick={onClearSelection}
              >
                <FaIcon
                  icon={faXmark}
                  className="tw-h-5 tw-w-5 tw-cursor-pointer tw-text-primary hover:tw-opacity-70"
                />
              </Tooltip>
            </>
          )}
          <Tooltip
            text={t('driverModule.chart-delete-selected')}
            blockedText={deleteBlockedText}
            onClick={() => {
              if (!deleteDisabled && hasSelection) onDelete()
            }}
          >
            <FaIcon
              icon={faTrash}
              className={`tw-h-5 tw-w-5 tw-text-red-600 ${
                hasSelection && !deleteDisabled
                  ? 'tw-cursor-pointer hover:tw-opacity-70'
                  : 'tw-cursor-not-allowed tw-opacity-40'
              }`}
            />
          </Tooltip>
          <Tooltip text={t('driverModule.refresh')} onClick={onRefresh}>
            <FaIcon
              icon={faRefreshSolid}
              className="tw-h-5 tw-w-5 tw-cursor-pointer tw-text-primary hover:tw-opacity-70"
            />
          </Tooltip>
          <Tooltip
            text={t('driverModule.chart-export-csv')}
            onClick={() => {
              if (!exportDisabled) onExport()
            }}
          >
            <FaIcon
              icon={faDownloadSolid}
              className={`tw-h-5 tw-w-5 tw-text-primary ${
                exportDisabled
                  ? 'tw-cursor-not-allowed tw-opacity-40'
                  : 'tw-cursor-pointer hover:tw-opacity-70'
              }`}
            />
          </Tooltip>
        </div>
      </div>
    </div>
  )
}

export default ChartDetailsActionBar
